import { Editor } from '@tiptap/core';
import { BubbleMenu as TiptapBubbleMenu } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	BoldOutline,
	Box,
	Divider,
	HStack,
	IconButton,
	Input,
	ItalicOutline,
	LinkAltOutline,
	ListOutline,
	OrderedListOutline,
	RefreshOutline,
	rem,
	StopOutline,
	Tooltip,
	UnderlineOutline,
	XOutline,
} from '@bonusly/design-system';

interface BubbleMenuProps {
	editor: Editor;
}

export function BubbleMenu({ editor }: BubbleMenuProps) {
	const [showLinkInput, setShowLinkInput] = useState(false);
	const [isRepeating, setIsRepeating] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const from = editor.state.selection.from;
	const to = editor.state.selection.to;
	const isRangeSelected = from !== to;

	// Reset link input when selection collapses (no longer a range)
	useEffect(() => {
		if (!isRangeSelected && showLinkInput) {
			if (inputRef.current) {
				inputRef.current.value = '';
			}
			setShowLinkInput(false);
		}
	}, [isRangeSelected, showLinkInput]);

	// Focus input when link input is shown
	useEffect(() => {
		if (showLinkInput) {
			inputRef.current?.focus();
		}
	}, [showLinkInput]);

	// Check if current selection is within a repeating block
	const checkIsRepeating = useCallback(() => {
		const { from, to } = editor.state.selection;
		let repeating = false;

		editor.state.doc.nodesBetween(from, to, (node) => {
			if (node.attrs.repeats === true) {
				repeating = true;
				return false;
			}
		});

		return repeating;
	}, [editor]);

	// Check if selection is inside a topic node (topics have their own repeat UI)
	const checkIsInTopicNode = useCallback(() => {
		const { from } = editor.state.selection;
		const resolvedPos = editor.state.doc.resolve(from);

		for (let depth = resolvedPos.depth; depth >= 0; depth--) {
			if (resolvedPos.node(depth).type.name === 'topic') {
				return true;
			}
		}
		return false;
	}, [editor]);

	// Update repeating state when selection changes
	useEffect(() => {
		const updateState = () => {
			setIsRepeating(checkIsRepeating());
		};

		editor.on('selectionUpdate', updateState);
		editor.on('transaction', updateState);

		// Initial check
		updateState();

		return () => {
			editor.off('selectionUpdate', updateState);
			editor.off('transaction', updateState);
		};
	}, [editor, checkIsRepeating]);

	// Handlers
	const handleToggleBold = () => {
		editor.chain().focus().toggleBold().run();
	};

	const handleToggleItalic = () => {
		editor.chain().focus().toggleItalic().run();
	};

	const handleToggleUnderline = () => {
		editor.chain().focus().toggleUnderline().run();
	};

	const handleToggleBulletList = () => {
		editor.chain().focus().toggleBulletList().run();
	};

	const handleToggleOrderedList = () => {
		editor.chain().focus().toggleOrderedList().run();
	};

	const handleToggleLink = () => {
		const { from, to } = editor.state.selection;
		let hasLink = false;

		editor.state.doc.nodesBetween(from, to, (node) => {
			if (node.isText && node.marks.length) {
				node.marks.forEach((mark) => {
					if (mark.type.name === 'link') {
						hasLink = true;
					}
				});
			}
		});

		if (hasLink) {
			editor.chain().focus().unsetLink().run();
		} else {
			setShowLinkInput(true);
		}
	};

	const handleAddLink = (e: React.FormEvent) => {
		e.preventDefault();

		let url = inputRef.current?.value ?? '';

		if (!url) {
			setShowLinkInput(false);
			return;
		}

		if (!url.startsWith('http://') && !url.startsWith('https://')) {
			url = `https://${url}`;
		}

		editor.chain().focus().setLink({ href: url }).run();
		setShowLinkInput(false);
	};

	const handleInputKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			setShowLinkInput(false);
		}
	};

	const handleToggleRepeat = () => {
		editor.commands.toggleRepeat();
	};

	const isInTopic = checkIsInTopicNode();

	return (
		<TiptapBubbleMenu
			editor={editor}
			shouldShow={({ from, to, state }) => {
				let show = from !== to;

				state.doc.nodesBetween(from, to, (node) => {
					if (node.type.name === 'image') {
						show = false;
					}
				});

				return show;
			}}
			tippyOptions={{
				animation: 'scale',
				duration: [200, 150],
				placement: 'top',
			}}
		>
			<HStack alignItems="stretch" bg="secondary.fg" borderRadius="md" gap={rem(2)} p={1} boxShadow="lg" width="fit-content">
				{showLinkInput ? (
					<>
						<Box as="form" flex={1} onSubmit={handleAddLink}>
							<Input
								border="none"
								boxShadow="none"
								color="white"
								fontSize="sm"
								display="flex"
								height="100%"
								onKeyDown={handleInputKeyDown}
								outline="none"
								placeholder="Enter URL..."
								ref={inputRef}
								w="full"
								minW="200px"
								_focus={{
									boxShadow: 'none',
									outline: 'none',
								}}
								_placeholder={{
									color: 'gray.300',
								}}
							/>
						</Box>

						<IconButton
							onClick={() => setShowLinkInput(false)}
							aria-label="Close"
							bg="none"
							borderRadius={4}
							boxSize={10}
							color="text.onColor"
							icon={<XOutline size="sm" />}
							minW="none"
							width="fit-content"
							height="fit-content"
							p={1.5}
							_hover={{
								bg: 'secondary.bgHover',
							}}
						/>
					</>
				) : (
					<>
						{/* Text formatting */}
						<IconButton
							onClick={handleToggleBold}
							aria-label="Bold"
							bg={editor.isActive('bold') ? 'secondary.bgHover' : 'none'}
							borderRadius={4}
							boxSize={10}
							height="fit-content"
							width="fit-content"
							minW="none"
							color="text.onColor"
							icon={<BoldOutline size="sm" />}
							p={1.5}
							_hover={{ bg: 'secondary.bgHover' }}
						/>

						<IconButton
							onClick={handleToggleItalic}
							aria-label="Italic"
							bg={editor.isActive('italic') ? 'secondary.bgHover' : 'none'}
							borderRadius={4}
							boxSize={10}
							height="fit-content"
							width="fit-content"
							minW="none"
							color="text.onColor"
							icon={<ItalicOutline size="sm" />}
							p={1.5}
							_hover={{ bg: 'secondary.bgHover' }}
						/>

						<IconButton
							onClick={handleToggleUnderline}
							aria-label="Underline"
							bg={editor.isActive('underline') ? 'secondary.bgHover' : 'none'}
							borderRadius={4}
							boxSize={10}
							height="fit-content"
							width="fit-content"
							minW="none"
							color="text.onColor"
							icon={<UnderlineOutline size="sm" />}
							p={1.5}
							_hover={{ bg: 'secondary.bgHover' }}
						/>

						<Divider orientation="vertical" borderColor="gray.600" height="auto" alignSelf="stretch" my={0.5} />

						{/* Lists */}
						<IconButton
							onClick={handleToggleBulletList}
							aria-label="Bullet List"
							bg={editor.isActive('bulletList') ? 'secondary.bgHover' : 'none'}
							borderRadius={4}
							boxSize={10}
							height="fit-content"
							width="fit-content"
							minW="none"
							color="text.onColor"
							icon={<ListOutline size="sm" />}
							p={1.5}
							_hover={{ bg: 'secondary.bgHover' }}
						/>

						<IconButton
							onClick={handleToggleOrderedList}
							aria-label="Ordered List"
							bg={editor.isActive('orderedList') ? 'secondary.bgHover' : 'none'}
							borderRadius={4}
							boxSize={10}
							height="fit-content"
							width="fit-content"
							minW="none"
							color="text.onColor"
							icon={<OrderedListOutline size="sm" />}
							p={1.5}
							_hover={{ bg: 'secondary.bgHover' }}
						/>

						<Divider orientation="vertical" borderColor="gray.600" height="auto" alignSelf="stretch" my={0.5} />

						{/* Link */}
						<IconButton
							onClick={handleToggleLink}
							aria-label="Link"
							bg={editor.isActive('link') ? 'secondary.bgHover' : 'none'}
							borderRadius={4}
							boxSize={10}
							height="fit-content"
							width="fit-content"
							minW="none"
							color="text.onColor"
							icon={<LinkAltOutline size="sm" />}
							p={1.5}
							_hover={{ bg: 'secondary.bgHover' }}
						/>

						{/* Repeat toggle - only show when NOT inside a topic node */}
						{!isInTopic && (
							<>
								<Divider orientation="vertical" borderColor="gray.600" height="auto" alignSelf="stretch" my={0.5} />

								<Tooltip label={isRepeating ? "Don't repeat in next meeting" : 'Repeat in next meeting'} placement="top" hasArrow>
									<IconButton
										onClick={handleToggleRepeat}
										aria-label={isRepeating ? "Don't repeat" : 'Repeat in next meeting'}
										bg={isRepeating ? 'secondary.bgHover' : 'none'}
										borderRadius={4}
										boxSize={10}
										height="fit-content"
										width="fit-content"
										minW="none"
										color="text.onColor"
										icon={isRepeating ? <StopOutline size="sm" /> : <RefreshOutline size="sm" />}
										p={1.5}
										_hover={{ bg: 'secondary.bgHover' }}
									/>
								</Tooltip>
							</>
						)}
					</>
				)}
			</HStack>
		</TiptapBubbleMenu>
	);
}
